// ShareView.jsx - OPTIMIZED for mobile performance
// Key optimizations:
// 1. Lazy Firebase imports cached at module level
// 2. Parallel data fetching instead of sequential
// 3. Progressive loading with skeleton states
// 4. Memoized expensive computations
// 5. Image lazy loading with placeholder
// 6. Reduced console logging (dev only)
// 7. Optimized canvas operations
// 8. Static logo instead of GIF

import React, { useState, useEffect, useRef, useCallback, useMemo, memo, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { X } from 'lucide-react';
// Consider using a static PNG/SVG logo instead of GIF for faster loading
const logo = '/LogoBlue.jpg';

// ============================================================================
// OPTIMIZATION 1: Cache Firebase imports at module level
// ============================================================================
let firebaseCache = null;
const getFirebase = async () => {
  if (!firebaseCache) {
    const [firestore, firebase] = await Promise.all([
      import('firebase/firestore'),
      import('../utils/firebase')
    ]);
    firebaseCache = { ...firestore, db: firebase.db };
  }
  return firebaseCache;
};

// ============================================================================
// OPTIMIZATION 2: Development-only logging
// ============================================================================
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

// ============================================================================
// CONSTANTS & HELPERS (unchanged but moved to top for clarity)
// ============================================================================
const sections = ["A", "B", "C", "D", "E", "F", "G", "H"];

const formatTime12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const isLightColor = (color) => {
  if (!color) return false;
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

const getPitchDisplayName = (pitchNumber, pitchNames) => {
  const possibleKeys = [
    `pitch-${pitchNumber}`,
    `pitch${pitchNumber}`,
    `Pitch ${pitchNumber}`,
    `Pitch-${pitchNumber}`,
    pitchNumber.toString(),
  ];
  
  for (const key of possibleKeys) {
    if (pitchNames && pitchNames[key]) {
      return pitchNames[key];
    }
  }
  
  return `Pitch ${pitchNumber}`;
};

const getSectionsForPitch = (config) => {
  if (!config) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  
  if (config.sectionsCount) {
    if (config.sectionsCount === 8) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    if (config.sectionsCount === 4) return ['A', 'B', 'C', 'D'];
    if (config.sectionsCount === 2) return ['A', 'C'];
    if (config.sectionsCount === 1) return ['A'];
  }
  
  if (config.sectionLayout) {
    if (config.sectionLayout === 'FULL' || config.sectionLayout === 'EIGHTH') return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    if (config.sectionLayout === 'HALF') return ['A', 'B', 'C', 'D'];
    if (config.sectionLayout === 'QUARTER') return ['A', 'C'];
    if (config.sectionLayout === 'SMALL') return ['A'];
  }
  
  return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
};

const getGridLayout = (config) => {
  if (!config) return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
  
  if (config.sectionsCount) {
    if (config.sectionsCount === 8) return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
    if (config.sectionsCount === 4) return { columns: '1fr 1fr', rows: '1fr 1fr' };
    if (config.sectionsCount === 2) return { columns: '1fr', rows: '1fr 1fr' };
    if (config.sectionsCount === 1) return { columns: '1fr', rows: '1fr' };
  }
  
  if (config.sectionLayout) {
    if (config.sectionLayout === 'FULL' || config.sectionLayout === 'EIGHTH') return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
    if (config.sectionLayout === 'HALF') return { columns: '1fr 1fr', rows: '1fr 1fr' };
    if (config.sectionLayout === 'QUARTER') return { columns: '1fr', rows: '1fr 1fr' };
    if (config.sectionLayout === 'SMALL') return { columns: '1fr', rows: '1fr' };
  }
  
  return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
};

// ============================================================================
// OPTIMIZATION 3: Skeleton Loading Components
// ============================================================================
const SkeletonPulse = memo(({ width = '100%', height = '20px', borderRadius = '4px', style = {} }) => (
  <div
    style={{
      width,
      height,
      borderRadius,
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'skeleton-pulse 1.5s ease-in-out infinite',
      ...style
    }}
  />
));

const MapViewSkeleton = memo(({ isMobile }) => (
  <div style={{
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    padding: isMobile ? '12px' : '20px'
  }}>
    <style>{`
      @keyframes skeleton-pulse {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto 20px',
      borderRadius: '12px',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #243665 0%, #3b82f6 100%)',
      padding: isMobile ? '16px' : '24px'
    }}>
      <SkeletonPulse height={isMobile ? '80px' : '100px'} style={{ marginBottom: '16px', opacity: 0.3 }} />
      <SkeletonPulse height="32px" width="60%" style={{ margin: '0 auto 12px', opacity: 0.3 }} />
      <SkeletonPulse height="20px" width="40%" style={{ margin: '0 auto', opacity: 0.3 }} />
    </div>
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: isMobile ? '16px' : '24px'
    }}>
      <SkeletonPulse height={isMobile ? '300px' : '500px'} borderRadius="8px" />
    </div>
  </div>
));

// ============================================================================
// FIELD MARKINGS (unchanged)
// ============================================================================
const renderFullPitchMarkings = () => (
  <>
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      border: '2px solid white',
      borderRadius: '4px'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '2px',
      right: '2px',
      top: '50%',
      height: '2px',
      transform: 'translateY(-50%)',
      backgroundColor: 'white'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: '80px',
      height: '80px',
      border: '2px solid white',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: '4px',
      height: '4px',
      backgroundColor: 'white',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}></div>
    
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '25%',
      right: '25%',
      height: '60px',
      border: '2px solid white',
      borderTop: 'none'
    }}></div>
    
    <div style={{
      position: 'absolute',
      bottom: '2px',
      left: '25%',
      right: '25%',
      height: '60px',
      border: '2px solid white',
      borderBottom: 'none'
    }}></div>
  </>
);

const renderHalfPitchMarkings = () => (
  <>
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      border: '2px solid white',
      borderRadius: '0'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '0',
      width: '80px',
      height: '40px',
      border: '2px solid white',
      borderRadius: '0 0 40px 40px',
      borderTop: 'none',
      transform: 'translateX(-50%)'
    }}></div>
  </>
);

const renderSimpleRectangle = () => (
  <div style={{
    position: 'absolute',
    top: '2px',
    left: '2px',
    right: '2px',
    bottom: '2px',
    border: '2px solid white',
    borderRadius: '4px'
  }}></div>
);

// ============================================================================
// OPTIMIZATION 4: Memoized CTA Component
// ============================================================================
const PitchalloCTA = memo(({ isMobile }) => (
  <div style={{
    maxWidth: '1200px',
    margin: '40px auto 20px',
    padding: isMobile ? '20px 16px' : '24px 32px',
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #1e3a5f 100%)',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden'
  }}>
    <div style={{
      position: 'absolute',
      top: '-20px',
      right: '-20px',
      width: '100px',
      height: '100px',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '50%'
    }} />
    <div style={{
      position: 'absolute',
      bottom: '-30px',
      left: '10%',
      width: '80px',
      height: '80px',
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: '50%'
    }} />
    
    <div style={{ position: 'relative', zIndex: 1 }}>
      <div style={{
        fontSize: isMobile ? '14px' : '16px',
        color: 'rgba(255, 255, 255, 0.9)',
        marginBottom: '8px',
        fontWeight: '500',
        letterSpacing: '0.5px'
      }}>
        ⚽ Love what you see?
      </div>
      
      <h3 style={{
        fontSize: isMobile ? '20px' : '26px',
        fontWeight: '700',
        color: '#ffffff',
        margin: '0 0 12px 0',
        lineHeight: 1.3
      }}>
        Get your club on Pitchallo today
      </h3>
      
      <p style={{
        fontSize: isMobile ? '14px' : '16px',
        color: 'rgba(255, 255, 255, 0.8)',
        margin: '0 0 20px 0',
        maxWidth: '500px',
        marginLeft: 'auto',
        marginRight: 'auto',
        lineHeight: 1.5
      }}>
        Simplify pitch allocation, save volunteer hours, and keep everyone informed with beautiful shareable schedules.
      </p>
      
      <a
        href="https://pitchallo.app"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: isMobile ? '12px 24px' : '14px 32px',
          backgroundColor: '#22c55e',
          color: '#ffffff',
          fontSize: isMobile ? '15px' : '17px',
          fontWeight: '600',
          borderRadius: '50px',
          textDecoration: 'none',
          boxShadow: '0 4px 14px rgba(34, 197, 94, 0.4)',
          transition: 'all 0.2s ease',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        Get Started Free
        <span style={{ fontSize: '18px' }}>→</span>
      </a>
      
      <div style={{
        marginTop: '16px',
        fontSize: isMobile ? '12px' : '13px',
        color: 'rgba(255, 255, 255, 0.6)'
      }}>
        No credit card required • Set up in minutes
      </div>
    </div>
  </div>
));

// ============================================================================
// OPTIMIZATION 5: Memoized Read-only PitchRenderer
// ============================================================================

// Zoomed Pitch Canvas for mobile confirmation view
const ZoomedPitchCanvas = memo(({ imageRef, pitchData, satelliteConfig, displayName, fullPitchData }) => {
  const zoomCanvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = zoomCanvasRef.current;
    const image = imageRef?.current;
    
    if (!canvas || !image || !pitchData || !satelliteConfig) return;
    
    const ctx = canvas.getContext('2d');
    const canvasSize = canvas.width;
    
    // Calculate zoom region - center on pitch with padding
    const padding = 1.5; // Show 1.5x the pitch size for context
    const pitchWidth = pitchData.width * padding;
    const pitchHeight = pitchData.height * padding;
    const maxDimension = Math.max(pitchWidth, pitchHeight);
    
    // Source region from the original image
    const srcX = pitchData.centerX - maxDimension / 2;
    const srcY = pitchData.centerY - maxDimension / 2;
    const srcSize = maxDimension;
    
    // Clamp source coordinates to image bounds and track the offset
    const actualSrcX = Math.max(0, srcX);
    const actualSrcY = Math.max(0, srcY);
    
    // Clear and draw zoomed portion
    ctx.clearRect(0, 0, canvasSize, canvasSize);
    
    try {
      ctx.drawImage(
        image,
        actualSrcX,
        actualSrcY,
        srcSize,
        srcSize,
        0,
        0,
        canvasSize,
        canvasSize
      );
      
      // Draw pitch boundary overlay
      const scale = canvasSize / srcSize;
      
      // Check for corners in pitchData or fullPitchData (corners define shape with implicit rotation)
      const corners = pitchData.corners || fullPitchData?.corners || fullPitchData?.boundaries?.corners;
      
      if (corners && corners.length === 4) {
        // Draw using corners - this handles rotation implicitly
        ctx.beginPath();
        
        const scaledCorners = corners.map(corner => ({
          x: (corner.x - actualSrcX) * scale,
          y: (corner.y - actualSrcY) * scale
        }));
        
        ctx.moveTo(scaledCorners[0].x, scaledCorners[0].y);
        for (let i = 1; i < scaledCorners.length; i++) {
          ctx.lineTo(scaledCorners[i].x, scaledCorners[i].y);
        }
        ctx.closePath();
        
        ctx.fillStyle = 'rgba(34, 197, 94, 0.35)';
        ctx.fill();
        
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 4;
        ctx.stroke();
        
        // Calculate center for label (average of corners)
        const labelX = scaledCorners.reduce((sum, c) => sum + c.x, 0) / 4;
        const labelY = scaledCorners.reduce((sum, c) => sum + c.y, 0) / 4;
        
        // Draw label
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 4;
        ctx.strokeText(displayName, labelX, labelY);
        
        ctx.fillStyle = '#ffffff';
        ctx.fillText(displayName, labelX, labelY);
      } else {
        // Fall back to center/width/height/rotation method
        const centerX = (pitchData.centerX - actualSrcX) * scale;
        const centerY = (pitchData.centerY - actualSrcY) * scale;
        const drawWidth = pitchData.width * scale;
        const drawHeight = pitchData.height * scale;
        const rotation = pitchData.rotation || 0;
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotation);
        
        // Draw filled rectangle with highlight
        ctx.fillStyle = 'rgba(34, 197, 94, 0.35)';
        ctx.fillRect(-drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        
        // Draw border
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 4;
        ctx.strokeRect(-drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        
        ctx.restore();
        
        // Draw label
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 4;
        ctx.strokeText(displayName, centerX, centerY);
        
        ctx.fillStyle = '#ffffff';
        ctx.fillText(displayName, centerX, centerY);
      }
      
    } catch (err) {
      console.error('Error drawing zoomed canvas:', err);
    }
  }, [imageRef, pitchData, satelliteConfig, displayName, fullPitchData]);
  
  return (
    <canvas
      ref={zoomCanvasRef}
      width={350}
      height={350}
      style={{
        width: '100%',
        height: '100%',
        display: 'block'
      }}
    />
  );
});

const ReadOnlyPitchRenderer = memo(({ 
  pitchSectionConfig, 
  timeSlot, 
  allocations, 
  date,
  pitchId,
  teams = [],
  groupSlots = null,
  pitchReferencePoints = null
}) => {
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  
  const currentSections = useMemo(() => getSectionsForPitch(pitchSectionConfig), [pitchSectionConfig]);
  const gridLayout = useMemo(() => getGridLayout(pitchSectionConfig), [pitchSectionConfig]);
  
  const getTeamName = useCallback((allocation) => {
    if (!allocation) return 'Unknown Team';
    if (allocation.teamName && typeof allocation.teamName === 'string') return allocation.teamName;
    if (allocation.team && typeof allocation.team === 'string') return allocation.team;
    return 'Unknown Team';
  }, []);
  
  const getTeamColor = useCallback((teamName) => {
    if (!teamName || !teams || !Array.isArray(teams) || teams.length === 0) return '#3b82f6';
    const team = teams.find(t => t.name === teamName);
    return team?.color || '#3b82f6';
  }, [teams]);
  
  const renderPitchMarkings = useMemo(() => {
    if (!pitchSectionConfig) return renderFullPitchMarkings();
    
    if (pitchSectionConfig.sectionsCount) {
      if (pitchSectionConfig.sectionsCount === 1) return renderSimpleRectangle();
      if (pitchSectionConfig.sectionsCount === 2) return renderSimpleRectangle();
      if (pitchSectionConfig.sectionsCount === 4) return renderHalfPitchMarkings();
      if (pitchSectionConfig.sectionsCount === 8) return renderFullPitchMarkings();
    }
    
    if (pitchSectionConfig.sectionLayout) {
      if (pitchSectionConfig.sectionLayout === 'SMALL') return renderSimpleRectangle();
      if (pitchSectionConfig.sectionLayout === 'QUARTER') return renderSimpleRectangle();
      if (pitchSectionConfig.sectionLayout === 'HALF') return renderHalfPitchMarkings();
      if (pitchSectionConfig.sectionLayout === 'FULL' || pitchSectionConfig.sectionLayout === 'EIGHTH') return renderFullPitchMarkings();
    }
    
    return renderFullPitchMarkings();
  }, [pitchSectionConfig]);

  return (
    <div className="pitch-visual" style={{
      position: 'relative',
      backgroundColor: '#dcfce7',
      border: '4px solid white',
      borderRadius: '8px',
      padding: '16px',
      width: '280px',
      height: '400px',
      margin: '0 auto'
    }}>
      {/* Pitch Reference Points - Outside overflow container */}
      {pitchReferencePoints && (
        <>
          {pitchReferencePoints.top && (
            <div style={{ position: 'absolute', top: '-28px', left: '50%', transform: 'translateX(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', zIndex: 20 }}>
              <span>{pitchReferencePoints.top}</span>
              <span style={{ fontSize: '10px', transform: 'rotate(180deg)', display: 'inline-block' }}>▼</span>
            </div>
          )}
          {pitchReferencePoints.bottom && (
            <div style={{ position: 'absolute', bottom: '-28px', left: '50%', transform: 'translateX(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', zIndex: 20 }}>
              <span>{pitchReferencePoints.bottom}</span>
              <span style={{ fontSize: '10px' }}>▼</span>
            </div>
          )}
          {pitchReferencePoints.left && (
            <div style={{ position: 'absolute', top: '50%', left: '-42px', transform: 'translateY(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', zIndex: 20 }}>
              <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{pitchReferencePoints.left}</span>
              <span style={{ fontSize: '10px', transform: 'rotate(90deg)' }}>▼</span>
            </div>
          )}
          {pitchReferencePoints.right && (
            <div style={{ position: 'absolute', top: '50%', right: '-42px', transform: 'translateY(-50%)', fontSize: '13px', fontWeight: '500', color: '#4b5563', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', zIndex: 20 }}>
              <span style={{ fontSize: '10px', transform: 'rotate(-90deg)' }}>▼</span>
              <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{pitchReferencePoints.right}</span>
            </div>
          )}
        </>
      )}
      
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#bbf7d0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {renderPitchMarkings}
      </div>
      
      <div className="pitch-sections" style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: gridLayout.columns,
        gridTemplateRows: gridLayout.rows,
        gap: '4px',
        height: '100%',
        zIndex: 10
      }}>
        {currentSections.map(sec => {
          // When grouping, find the first allocation for this section across all slots in the group
          let allocation = null;
          let allocationKey = null;
          const slotsToCheck = groupSlots || [timeSlot];
          for (const slot of slotsToCheck) {
            const key = `${date}-${slot}-${pitchId}-${sec}`;
            if (allocations[key]) {
              allocation = allocations[key];
              allocationKey = key;
              break;
            }
          }
          const teamName = getTeamName(allocation);
          const teamColor = allocation 
            ? (allocation.colour || allocation.color || allocation.teamColor || getTeamColor(allocation.team)) 
            : '#ffffff';
          
          let backgroundColor, borderColor, textColor;
          
          if (allocation) {
            backgroundColor = teamColor + '90';
            borderColor = teamColor;
            textColor = isLightColor(teamColor) ? '#000' : '#fff';
          } else {
            backgroundColor = 'rgba(255,255,255,0.1)';
            borderColor = 'rgba(255,255,255,0.5)';
            textColor = '#374151';
          }
          
          return (
            <div 
              key={sec}
              className="pitch-section"
              style={{
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: currentSections.length === 1 ? '20px' : currentSections.length === 2 ? '16px' : '12px',
                fontWeight: '500',
                transition: 'all 0.2s',
                position: 'relative',
                padding: currentSections.length === 1 ? '8px' : '2px',
                textAlign: 'center',
                cursor: allocation ? 'pointer' : 'default',
                backgroundColor,
                borderColor,
                color: textColor
              }}
              onClick={() => {
                if (!allocation) return;
                setSelectedAllocation({
                  ...allocation,
                  section: sec,
                  allocationKey: allocationKey,
                  teamName,
                  pitchId
                });
              }}
              title={allocation ? `${teamName} - Tap for details` : `Section ${sec} - Available`}
            >
              {allocation && (
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  width: currentSections.length === 1 ? '20px' : '14px',
                  height: currentSections.length === 1 ? '20px' : '14px',
                  backgroundColor: (allocation.type === 'training' || allocation.allocationType === 'training') ? '#3b82f6' : '#ef4444',
                  color: 'white',
                  fontSize: currentSections.length === 1 ? '12px' : '8px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2px',
                  zIndex: 20
                }}>
                  {(allocation.type === 'training' || allocation.allocationType === 'training') ? 'T' : 'M'}
                </div>
              )}
              
              <div style={{
                fontSize: currentSections.length === 1 ? '24px' : currentSections.length === 2 ? '16px' : '12px',
                opacity: 0.75,
                marginBottom: currentSections.length === 1 ? '8px' : '4px',
                fontWeight: 'bold'
              }}>
                {sec}
              </div>
              
              <div style={{
                textAlign: 'center',
                padding: '0 4px',
                fontSize: currentSections.length === 1 ? '18px' : currentSections.length === 2 ? '14px' : '12px',
                lineHeight: 1.2
              }}>
                {allocation ? teamName : ''}
              </div>
              
              {allocation && allocation.duration && (
                <div style={{
                  fontSize: currentSections.length === 1 ? '14px' : currentSections.length === 2 ? '12px' : '10px',
                  opacity: 0.8,
                  marginTop: currentSections.length === 1 ? '4px' : '2px'
                }}>
                  {allocation.duration}min
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Allocation Info Modal */}
      {selectedAllocation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
          onClick={() => setSelectedAllocation(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedAllocation(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af'
              }}
            >
              <X size={20} />
            </button>
            
            <div style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              marginBottom: '16px',
              backgroundColor: (selectedAllocation.type === 'training' || selectedAllocation.allocationType === 'training') 
                ? '#dbeafe' 
                : '#fee2e2',
              color: (selectedAllocation.type === 'training' || selectedAllocation.allocationType === 'training') 
                ? '#1d4ed8' 
                : '#dc2626'
            }}>
              {(selectedAllocation.type === 'training' || selectedAllocation.allocationType === 'training') ? 'Training' : 'Match'}
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '4px'
            }}>
              {selectedAllocation.teamName || selectedAllocation.team || 'Unknown Team'}
            </h3>
            
            {selectedAllocation.fixtureData?.awayTeam && (
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                marginBottom: '16px'
              }}>
                vs {selectedAllocation.fixtureData.awayTeam}
              </p>
            )}
            
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '10px',
              padding: '16px',
              marginTop: '12px'
            }}>
              {(selectedAllocation.startTime || selectedAllocation.endTime) && (
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  <strong>Time:</strong>{' '}
                  {selectedAllocation.startTime ? formatTime12h(selectedAllocation.startTime) : ''}
                  {selectedAllocation.startTime && selectedAllocation.endTime ? ' – ' : ''}
                  {selectedAllocation.endTime ? formatTime12h(selectedAllocation.endTime) : ''}
                </div>
              )}
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                <strong>Section:</strong> {selectedAllocation.section}
              </div>
              {selectedAllocation.duration && (
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  <strong>Duration:</strong> {selectedAllocation.duration} minutes
                </div>
              )}
              {selectedAllocation.notes && (
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Notes:</strong> {selectedAllocation.notes}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================================================
// OPTIMIZATION 6: Optimized data loading with parallel queries
// ============================================================================
const loadPitchNames = async (clubId) => {
  const { doc, getDoc, db } = await getFirebase();
  
  try {
    const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data().pitchNames || {};
    }
    return {};
  } catch (error) {
    console.error('Error loading pitch names:', error);
    return {};
  }
};

const loadBrandingSettings = async (clubId) => {
  const { doc, getDoc, db } = await getFirebase();
  
  try {
    const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data().branding || {};
    }
    return {};
  } catch (error) {
    console.error('Error loading branding settings:', error);
    return {};
  }
};

const loadPitchConfigurations = async (clubId) => {
  const { doc, getDoc, db } = await getFirebase();
  
  try {
    const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data().pitchSectionConfigs || {};
    }
    return {};
  } catch (error) {
    console.error('Error loading pitch configurations:', error);
    return {};
  }
};

// OPTIMIZATION: Parallel allocation queries for all pitches
const loadAllocationsInParallel = async (clubId, pitchesFromConfig, date, firebase) => {
  const { collection, query, where, getDocs, db } = firebase;
  
  const allocationPromises = pitchesFromConfig.map(async (pitch) => {
    let pitchNumber = pitch.pitchNumber;
    
    if (!pitchNumber && pitch.id) {
      pitchNumber = pitch.id.replace(/\D/g, '');
    }
    
    if (!pitchNumber && pitch.name) {
      const nameMatch = pitch.name.match(/\d+/);
      pitchNumber = nameMatch ? nameMatch[0] : null;
    }
    
    if (!pitchNumber) return {};
    
    const pitchIdFormat = `pitch-${pitchNumber}`;
    
    try {
      const allocationsRef = collection(db, 'clubs', clubId, 'allocations', pitchIdFormat, 'items');
      const allocationsQ = query(allocationsRef, where('date', '==', date));
      const allocationsSnapshot = await getDocs(allocationsQ);
      
      const pitchAllocations = {};
      allocationsSnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        pitchAllocations[docSnapshot.id] = {
          ...data,
          type: data.allocationType || data.type || 'training'
        };
      });
      
      return pitchAllocations;
    } catch (err) {
      devLog(`Error querying allocations for ${pitchIdFormat}:`, err);
      return {};
    }
  });
  
  const results = await Promise.all(allocationPromises);
  return results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

// Main function to get shared allocation data - OPTIMIZED
const getSharedAllocation = async (shareId) => {
  try {
    const firebase = await getFirebase();
    const { doc, getDoc, collection, getDocs, updateDoc, increment, db } = firebase;
    
    devLog('Loading share metadata for:', shareId);
    
    const shareDoc = await getDoc(doc(db, 'sharedAllocations', shareId));
    
    if (!shareDoc.exists()) {
      throw new Error('Share link not found or has expired');
    }
    
    const shareMetadata = shareDoc.data();
    
    if (shareMetadata.expiresAt) {
      const expiryDate = new Date(shareMetadata.expiresAt);
      if (expiryDate < new Date()) {
        throw new Error('This share link has expired');
      }
    }
    
    const { clubId, date, pitchId, facilityId, facilityName } = shareMetadata;
    
    if (!clubId || !date) {
      throw new Error('Invalid share link - missing required parameters');
    }
    
    // Fire and forget view counter update
    updateDoc(doc(db, 'sharedAllocations', shareId), {
      views: increment(1),
      lastViewed: new Date().toISOString()
    }).catch(() => {});
    
    const clubDoc = await getDoc(doc(db, 'clubs', clubId));
    if (!clubDoc.exists()) {
      throw new Error('Club not found');
    }
    
    const clubData = clubDoc.data();
    
    let pitchesFromConfig = [];
    let facilityData = null;
    
    // Load facility or club pitches
    if (facilityId && facilityId !== 'default') {
      try {
        const facilityRef = doc(db, 'clubs', clubId, 'facilities', facilityId);
        const facilityDoc = await getDoc(facilityRef);
        
        if (facilityDoc.exists()) {
          facilityData = { id: facilityDoc.id, ...facilityDoc.data() };
          pitchesFromConfig = facilityData.pitches || [];
        }
      } catch (err) {
        devLog('Error loading facility:', err);
      }
    }
    
    if (pitchesFromConfig.length === 0) {
      pitchesFromConfig = clubData.pitches || clubData.satelliteConfig?.pitchBoundaries || [];
    }
    
    if (pitchesFromConfig.length === 0) {
      try {
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists()) {
          const pitchNames = settingsDoc.data().pitchNames || {};
          Object.keys(pitchNames).forEach(pitchKey => {
            const pitchNumber = pitchKey.replace(/\D/g, '');
            if (pitchNumber) {
              pitchesFromConfig.push({ pitchNumber });
            }
          });
        }
      } catch (err) {
        devLog('Error loading pitch names from settings:', err);
      }
    }
    
    // OPTIMIZATION: Load all allocations in parallel
    const allocations = await loadAllocationsInParallel(clubId, pitchesFromConfig, date, firebase);
    
    devLog('Total allocations loaded:', Object.keys(allocations).length);
    
    const processedPitchBoundaries = pitchesFromConfig.map((pitch, index) => ({
      pitchNumber: (pitch.pitchNumber || (index + 1)).toString(),
      sizeType: pitch.sizeType || 'large',
    }));
    
    const hasTraining = Object.values(allocations).some(a => a.type === 'training' || a.allocationType === 'training');
    const hasMatch = Object.values(allocations).some(a => a.type === 'match' || a.allocationType === 'match');
    let allocationType = 'Pitch';
    if (hasTraining && !hasMatch) allocationType = 'Training';
    if (hasMatch && !hasTraining) allocationType = 'Match Day';
    if (hasTraining && hasMatch) allocationType = 'Mixed';
    
    const satelliteConfig = {
      imageUrl: facilityData?.satelliteImageUrl || facilityData?.satelliteImage || 
                clubData.satelliteImage || clubData.satelliteConfig?.imageUrl || null,
      imageWidth: facilityData?.imageWidth || clubData.imageWidth || clubData.satelliteConfig?.imageWidth || 1600,
      imageHeight: facilityData?.imageHeight || clubData.imageHeight || clubData.satelliteConfig?.imageHeight || 1200,
      pitches: pitchesFromConfig
    };
    
    return {
      allocations,
      allocationType,
      date,
      pitchIdFromUrl: pitchId,
      clubId,
      clubName: clubData.name,
      facilityId: facilityId || null,
      facilityName: facilityData?.name || facilityName || null,
      start: shareMetadata.start || 6,
      end: shareMetadata.end || 22,
      pitchBoundaries: processedPitchBoundaries,
      satelliteConfig
    };

  } catch (error) {
    console.error('Error fetching shared allocation:', error);
    throw new Error(error.message || 'Failed to load shared allocation');
  }
};

// ============================================================================
// MAIN SHAREVIEW COMPONENT
// ============================================================================
function ShareView() {
  const { shareId } = useParams();
  const [sharedData, setSharedData] = useState(null);
  const [pitchNames, setPitchNames] = useState({});
  const [pitchConfigs, setPitchConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPitch, setSelectedPitch] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState('map');
  const [branding, setBranding] = useState({});
  const [teamSettings, setTeamSettings] = useState([]);
  
  const [allFacilities, setAllFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [currentFacilityData, setCurrentFacilityData] = useState(null);
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const mapScrollContainerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teamFilterMenuOpen, setTeamFilterMenuOpen] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  
  // Mobile zoom state for pitch selection confirmation
  const [zoomedPitch, setZoomedPitch] = useState(null); // { pitchNumber, pitchData, displayName }

  // Team filtering functions
  const toggleTeamSelection = useCallback((teamName) => {
    setSelectedTeams(prev => 
      prev.includes(teamName) 
        ? prev.filter(t => t !== teamName)
        : [...prev, teamName]
    );
  }, []);

  const clearTeamFilters = useCallback(() => setSelectedTeams([]), []);
  
  const selectAllTeams = useCallback(() => {
    setSelectedTeams(teamSettings.map(t => t.name));
  }, [teamSettings]);

  // OPTIMIZATION: Memoized available teams
  const availableTeams = useMemo(() => {
    if (!sharedData?.allocations) return [];
    
    const teamSet = new Set();
    Object.values(sharedData.allocations).forEach(allocation => {
      if (allocation.team) teamSet.add(allocation.team);
    });
    
    return Array.from(teamSet).sort();
  }, [sharedData?.allocations]);

  // Click outside handler for dropdown
  useEffect(() => {
    if (!teamFilterMenuOpen) return;
    
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-team-filter-dropdown]')) {
        setTeamFilterMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [teamFilterMenuOpen]);

  // Cleanup highlight timeout on unmount
  useEffect(() => {
    // Reset zoom when switching views
    return () => setZoomedPitch(null);
  }, []);

  // Resize handler - debounced
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768);
        
        if (imageRef.current && sharedData?.satelliteConfig) {
          const imgWidth = sharedData.satelliteConfig.imageWidth || imageRef.current.naturalWidth;
          const imgHeight = sharedData.satelliteConfig.imageHeight || imageRef.current.naturalHeight;
          
          if (imgWidth && imgHeight) {
            const maxWidth = window.innerWidth < 768 ? window.innerWidth - 40 : 1000;
            const maxHeight = window.innerWidth < 768 ? 400 : 700;
            const aspectRatio = imgWidth / imgHeight;
            
            let width = maxWidth;
            let height = width / aspectRatio;
            
            if (height > maxHeight) {
              height = maxHeight;
              width = height * aspectRatio;
            }
            
            setCanvasSize({ 
              width: Math.floor(Math.max(width, window.innerWidth < 768 ? 280 : 400)), 
              height: Math.floor(Math.max(height, window.innerWidth < 768 ? 200 : 300)) 
            });
          }
        }
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [sharedData?.satelliteConfig]);

  // Main data loading
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setPitchNames({});
        setBranding({});
        setSelectedPitch(null);
        setViewMode('map');
        setSharedData(null);
        
        const data = await getSharedAllocation(shareId);
        setSharedData(data);
        
        if (data?.clubId) {
          // OPTIMIZATION: Load all settings in parallel
          const [names, brandingData, configs, teams, refPoints] = await Promise.all([
            loadPitchNames(data.clubId),
            loadBrandingSettings(data.clubId),
            loadPitchConfigurations(data.clubId),
            (async () => {
              const { doc, getDoc, db } = await getFirebase();
              const settingsRef = doc(db, 'clubs', data.clubId, 'settings', 'general');
              const settingsDoc = await getDoc(settingsRef);
              
              if (settingsDoc.exists()) {
                const teamsData = settingsDoc.data().teams;
                if (teamsData && typeof teamsData === 'object' && !Array.isArray(teamsData)) {
                  return Object.entries(teamsData).map(([teamName, teamData]) => ({
                    name: teamData.name || teamName,
                    color: teamData.color || '#3b82f6',
                    ...teamData
                  }));
                }
                return teamsData || [];
              }
              return [];
            })(),
            (async () => {
              const { doc, getDoc, db } = await getFirebase();
              const settingsRef = doc(db, 'clubs', data.clubId, 'settings', 'general');
              const settingsDoc = await getDoc(settingsRef);
              return settingsDoc.exists() ? (settingsDoc.data().pitchReferencePoints || {}) : {};
            })()
          ]);
          
          setPitchNames(names);
          setBranding(brandingData);
          setPitchConfigs(configs);
          setTeamSettings(teams);
          
          // Store reference points in sharedData
          if (data) {
            setSharedData({
              ...data,
              pitchReferencePoints: refPoints
            });
          }
        }
        
      } catch (err) {
        console.error('Error loading shared data:', err);
        setError(err.message || 'Failed to load shared allocation');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [shareId]);

  // Load facilities for switching
  useEffect(() => {
    if (!sharedData?.clubId) return;
    
    const loadAllFacilities = async () => {
      try {
        const { collection, getDocs, db } = await getFirebase();
        
        const facilitiesRef = collection(db, 'clubs', sharedData.clubId, 'facilities');
        const facilitiesSnap = await getDocs(facilitiesRef);
        
        if (!facilitiesSnap.empty) {
          const facilitiesData = [];
          facilitiesSnap.forEach((doc) => {
            facilitiesData.push({ id: doc.id, ...doc.data() });
          });
          
          facilitiesData.sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });
          
          setAllFacilities(facilitiesData);
          
          if (sharedData.facilityId) {
            const sharedFacility = facilitiesData.find(f => f.id === sharedData.facilityId);
            if (sharedFacility) {
              setSelectedFacilityId(sharedFacility.id);
              setCurrentFacilityData(sharedFacility);
            }
          } else {
            const primaryFacility = facilitiesData.find(f => f.isPrimary) || facilitiesData[0];
            if (primaryFacility) {
              setSelectedFacilityId(primaryFacility.id);
              setCurrentFacilityData(primaryFacility);
            }
          }
        }
      } catch (err) {
        devLog('Error loading facilities for switching:', err);
      }
    };
    
    loadAllFacilities();
  }, [sharedData?.clubId, sharedData?.facilityId]);

  // Handle facility switching
  const handleFacilityChange = useCallback(async (newFacilityId) => {
    if (!newFacilityId || newFacilityId === selectedFacilityId) return;
    
    const facility = allFacilities.find(f => f.id === newFacilityId);
    if (!facility) return;
    
    setSelectedFacilityId(newFacilityId);
    setCurrentFacilityData(facility);
    setImageLoaded(false);
    setSelectedPitch(null);
    setViewMode('map');
    setZoomedPitch(null); // Clear any zoomed pitch
    
    try {
      const firebase = await getFirebase();
      const facilityPitches = facility.pitches || [];
      
      // OPTIMIZATION: Parallel loading
      const newAllocations = await loadAllocationsInParallel(
        sharedData.clubId, 
        facilityPitches, 
        sharedData.date, 
        firebase
      );
      
      setSharedData(prev => ({
        ...prev,
        facilityName: facility.name,
        allocations: newAllocations,
        satelliteConfig: {
          imageUrl: facility.satelliteImageUrl || facility.satelliteImage || null,
          imageWidth: facility.imageWidth || 1600,
          imageHeight: facility.imageHeight || 1200,
          pitches: facility.pitches || []
        }
      }));
    } catch (err) {
      console.error('Error loading allocations for facility:', err);
      setSharedData(prev => ({
        ...prev,
        facilityName: facility.name,
        satelliteConfig: {
          imageUrl: facility.satelliteImageUrl || facility.satelliteImage || null,
          imageWidth: facility.imageWidth || 1600,
          imageHeight: facility.imageHeight || 1200,
          pitches: facility.pitches || []
        }
      }));
    }
  }, [selectedFacilityId, allFacilities, sharedData?.clubId, sharedData?.date]);

  // Derived values
  const date = sharedData?.date || new Date().toISOString().split('T')[0];
  const clubName = sharedData?.clubName || 'Soccer Club';
  const facilityName = currentFacilityData?.name || sharedData?.facilityName || null;
  const allocationType = sharedData?.allocationType || 'Pitch';
  
  // OPTIMIZATION: Memoized pitch boundaries
  const pitchBoundaries = useMemo(() => {
    if (!sharedData?.satelliteConfig?.pitches) return [];
    
    return sharedData.satelliteConfig.pitches.map((pitch, index) => {
      let pitchNumber = pitch.pitchNumber || pitch.id || null;
      
      if (!pitchNumber && pitch.name) {
        const nameMatch = pitch.name.match(/\d+/);
        pitchNumber = nameMatch ? nameMatch[0] : String(index + 1);
      }
      
      if (!pitchNumber) pitchNumber = String(index + 1);
      
      if (typeof pitchNumber === 'string' && pitchNumber.toLowerCase().includes('pitch')) {
        pitchNumber = pitchNumber.replace(/pitch-?/gi, '').trim();
      }
      
      return {
        pitchNumber: pitchNumber.toString(),
        name: pitch.name || `Pitch ${pitchNumber}`,
        centerX: pitch.centerX,
        centerY: pitch.centerY,
        width: pitch.width,
        height: pitch.height,
        rotation: pitch.rotation
      };
    });
  }, [sharedData?.satelliteConfig?.pitches]);

  // Branding values
  const hasBrandingData = branding && Object.keys(branding).length > 0;
  const clubLogoUrl = hasBrandingData ? (branding.clubLogoUrl || null) : null;
  const primaryColor = hasBrandingData ? (branding.primaryColor || '#243665') : '#243665';
  const secondaryColor = hasBrandingData ? (branding.secondaryColor || '#3b82f6') : '#3b82f6';
  const sponsorLogoUrl = hasBrandingData ? (branding.sponsorLogoUrl || null) : null;
  const rawSponsorUrl = hasBrandingData ? (branding.sponsorUrl || '') : '';
  const sponsorUrl = rawSponsorUrl && !rawSponsorUrl.startsWith('http') 
    ? `https://${rawSponsorUrl}` 
    : rawSponsorUrl;
  const textColor = isLightColor(primaryColor) ? '#000000' : '#ffffff';

  // Canvas functions - with mobile zoom enhancement
  const calculateCanvasSize = useCallback((imgWidth, imgHeight) => {
    if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0) {
      return { width: window.innerWidth < 768 ? window.innerWidth - 40 : 1000, height: window.innerWidth < 768 ? 350 : 700 };
    }

    const isMobileView = window.innerWidth < 768;
    
    if (isMobileView) {
      // On mobile: make canvas LARGER for zoomed view (will be scrollable)
      // Use 1.8x scale for better pitch visibility
      const mobileScale = 1.8;
      const baseWidth = window.innerWidth - 40;
      const aspectRatio = imgWidth / imgHeight;
      
      const width = Math.floor(baseWidth * mobileScale);
      const height = Math.floor(width / aspectRatio);
      
      return { width, height };
    } else {
      // Desktop: normal sizing
      const maxWidth = 1000;
      const maxHeight = 700;
      const aspectRatio = imgWidth / imgHeight;
      
      let width = maxWidth;
      let height = width / aspectRatio;
      
      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }
      
      const minWidth = 400;
      const minHeight = 300;
      
      if (width < minWidth) width = minWidth;
      if (height < minHeight) height = minHeight;
      
      return { width: Math.floor(width), height: Math.floor(height) };
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    if (imageRef.current && sharedData?.satelliteConfig) {
      const imgWidth = sharedData.satelliteConfig.imageWidth || imageRef.current.naturalWidth;
      const imgHeight = sharedData.satelliteConfig.imageHeight || imageRef.current.naturalHeight;
      
      const size = calculateCanvasSize(imgWidth, imgHeight);
      setCanvasSize(size);
      setImageLoaded(true);
    }
  }, [sharedData?.satelliteConfig, calculateCanvasSize]);

  const drawPitchBoundary = useCallback((ctx, pitch, scaleX, scaleY, index) => {
    try {
      const pitchData = pitch.boundaries ? pitch.boundaries : pitch;
      
      const centerX = pitchData.centerX * scaleX;
      const centerY = pitchData.centerY * scaleY;
      
      if (pitchData.corners && pitchData.corners.length === 4) {
        ctx.fillStyle = 'rgba(34, 197, 94, 0.3)';
        ctx.strokeStyle = '#16a34a';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        ctx.moveTo(pitchData.corners[0].x * scaleX, pitchData.corners[0].y * scaleY);
        for (let i = 1; i < 4; i++) {
          ctx.lineTo(pitchData.corners[i].x * scaleX, pitchData.corners[i].y * scaleY);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      } else {
        const width = pitchData.width * scaleX;
        const height = pitchData.height * scaleY;
        const rotation = pitchData.rotation || 0;
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotation);
        
        ctx.fillStyle = 'rgba(34, 197, 94, 0.3)';
        ctx.fillRect(-width / 2, -height / 2, width, height);
        
        ctx.strokeStyle = '#16a34a';
        ctx.lineWidth = 2;
        ctx.strokeRect(-width / 2, -height / 2, width, height);
        
        ctx.rotate(-rotation);
      }
      
      let displayName;
      if (pitch.name) {
        displayName = pitch.name;
      } else if (pitch.pitchNumber) {
        displayName = getPitchDisplayName(pitch.pitchNumber, pitchNames);
      } else if (pitch.id) {
        const pitchNum = pitch.id.replace('pitch-', '').replace('pitch', '');
        displayName = getPitchDisplayName(pitchNum, pitchNames);
      } else {
        displayName = getPitchDisplayName(index + 1, pitchNames);
      }
      
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.strokeText(displayName, centerX, centerY);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillText(displayName, centerX, centerY);
      
      if (!(pitchData.corners && pitchData.corners.length === 4)) {
        ctx.restore();
      }
    } catch (error) {
      console.error('Error drawing pitch boundary:', error);
    }
  }, [pitchNames]);

  // OPTIMIZATION: Draw canvas without setTimeout
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !imageLoaded || !canvasSize.width || !canvasSize.height) {
      return;
    }

    try {
      const ctx = canvas.getContext('2d');
      
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      const scaleX = canvas.width / (sharedData.satelliteConfig.imageWidth || image.naturalWidth);
      const scaleY = canvas.height / (sharedData.satelliteConfig.imageHeight || image.naturalHeight);

      if (sharedData.satelliteConfig.pitches && sharedData.satelliteConfig.pitches.length > 0) {
        sharedData.satelliteConfig.pitches.forEach((pitch, index) => {
          drawPitchBoundary(ctx, pitch, scaleX, scaleY, index);
        });
      }
      
      canvas.style.display = 'block';
      canvas.style.visibility = 'visible';
      
    } catch (error) {
      console.error('Error drawing canvas:', error);
    }
  }, [imageLoaded, sharedData?.satelliteConfig, canvasSize, drawPitchBoundary]);

  // OPTIMIZATION: Use requestAnimationFrame instead of setTimeout
  useEffect(() => {
    if (imageLoaded && canvasRef.current && canvasSize.width > 0 && canvasSize.height > 0) {
      requestAnimationFrame(drawCanvas);
      
      // Center the scroll position on mobile after a brief delay
      if (isMobile && mapScrollContainerRef.current) {
        setTimeout(() => {
          const container = mapScrollContainerRef.current;
          if (container) {
            // Center horizontally
            const scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
            // Start slightly from top to show more pitches
            const scrollTop = (container.scrollHeight - container.clientHeight) * 0.3;
            container.scrollTo({
              left: scrollLeft,
              top: scrollTop,
              behavior: 'auto'
            });
          }
        }, 100);
      }
    }
  }, [imageLoaded, sharedData?.satelliteConfig, drawCanvas, canvasSize, isMobile]);

  // Handle canvas click/touch - zooms on mobile, navigates on desktop
  const handleCanvasClick = useCallback((e) => {
    if (!sharedData?.satelliteConfig?.pitches || !canvasSize.width || !canvasSize.height) return;
    
    // Prevent double-firing from both touch and click events
    if (e.type === 'click' && e.pointerType === 'touch') return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const clickX = ((clientX - rect.left) / canvasSize.width) * (sharedData.satelliteConfig.imageWidth || imageRef.current?.naturalWidth || 1);
    const clickY = ((clientY - rect.top) / canvasSize.height) * (sharedData.satelliteConfig.imageHeight || imageRef.current?.naturalHeight || 1);

    const clickedPitch = sharedData.satelliteConfig.pitches.find(pitch => {
      const pitchData = pitch.boundaries ? pitch.boundaries : pitch;
      
      const dx = clickX - pitchData.centerX;
      const dy = clickY - pitchData.centerY;
      const rotation = pitchData.rotation || 0;
      
      const cos = Math.cos(-rotation);
      const sin = Math.sin(-rotation);
      const localX = dx * cos - dy * sin;
      const localY = dx * sin + dy * cos;
      
      const halfWidth = pitchData.width / 2;
      const halfHeight = pitchData.height / 2;
      
      return Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight;
    });

    if (clickedPitch) {
      let pitchNumber = clickedPitch.pitchNumber || clickedPitch.id;
      
      if (!pitchNumber && clickedPitch.name) {
        const nameMatch = clickedPitch.name.match(/\d+/);
        pitchNumber = nameMatch ? nameMatch[0] : null;
      }
      
      if (pitchNumber) {
        if (typeof pitchNumber === 'string' && pitchNumber.toLowerCase().includes('pitch')) {
          pitchNumber = pitchNumber.replace(/pitch-?/gi, '').trim();
        }
        
        const pitchStr = pitchNumber.toString();
        const displayName = getPitchDisplayName(pitchStr, pitchNames);
        const pitchData = clickedPitch.boundaries ? clickedPitch.boundaries : clickedPitch;
        
        if (isMobile) {
          // On mobile: show zoomed confirmation view
          setZoomedPitch({
            pitchNumber: pitchStr,
            pitchData: pitchData,
            displayName: displayName,
            fullPitchData: clickedPitch
          });
        } else {
          // On desktop: navigate directly
          setSelectedPitch(pitchStr);
          setViewMode('pitch');
        }
      }
    }
  }, [sharedData?.satelliteConfig, canvasSize, isMobile, pitchNames]);

  // OPTIMIZATION: Memoized getPitchAllocations
  const getPitchAllocations = useCallback((pitchNumber) => {
    if (!sharedData?.allocations) return {};
    
    const pitchAllocations = {};
    
    Object.entries(sharedData.allocations).forEach(([key, allocation]) => {
      const pitchRegex = new RegExp(`-pitch-${pitchNumber}-[A-H]$`);
      const altPitchRegex = new RegExp(`-pitch${pitchNumber}-[A-H]$`);
      const capitalPitchRegex = new RegExp(`-Pitch-${pitchNumber}-[A-H]$`, 'i');
      const looseMatch = key.includes(`pitch-${pitchNumber}`) || 
                         key.includes(`pitch${pitchNumber}`) ||
                         key.includes(`Pitch ${pitchNumber}`) ||
                         key.includes(`Pitch-${pitchNumber}`);
      
      if (pitchRegex.test(key) || altPitchRegex.test(key) || capitalPitchRegex.test(key) || looseMatch) {
        if (selectedTeams.length === 0 || selectedTeams.includes(allocation.team)) {
          pitchAllocations[key] = allocation;
        }
      }
    });
    
    return pitchAllocations;
  }, [sharedData?.allocations, selectedTeams]);

  // Get unique time slots for a pitch
  const getTimeSlotsForPitch = useCallback((pitchNumber) => {
    const allocations = getPitchAllocations(pitchNumber);
    const timeSlots = new Set();
    
    Object.keys(allocations).forEach(key => {
      const parts = key.split('-');
      parts.forEach(part => {
        if (part && part.match(/^\d{2}:\d{2}$/)) {
          timeSlots.add(part);
        }
      });
    });
    
    return Array.from(timeSlots).sort();
  }, [getPitchAllocations]);

  // Group 15-min slots into buckets of intervalMinutes
  const groupTimeSlots = useCallback((timeSlots, interval) => {
    if (interval === 15) return timeSlots.map(slot => {
      const [h, m] = slot.split(':').map(Number);
      let em = m + 15, eh = h;
      if (em >= 60) { em -= 60; eh += 1; }
      return { label: `${slot} - ${eh.toString().padStart(2,'0')}:${em.toString().padStart(2,'0')}`, slots: [slot] };
    });

    const step = interval / 15; // slots per group: 30min=2, 60min=4
    const groups = [];

    for (let i = 0; i < timeSlots.length; i += step) {
      const groupSlots = timeSlots.slice(i, i + step);
      const firstSlot = groupSlots[0];
      const lastSlot = groupSlots[groupSlots.length - 1];

      // End time = last slot + 15 min
      const [lh, lm] = lastSlot.split(':').map(Number);
      let endM = lm + 15;
      let endH = lh;
      if (endM >= 60) { endM -= 60; endH += 1; }
      const endLabel = `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;

      groups.push({
        label: `${firstSlot} - ${endLabel}`,
        slots: groupSlots
      });
    }

    return groups;
  }, []);

  // Render team filter dropdown (memoized)
  const renderTeamFilter = useCallback(() => {
    if (availableTeams.length === 0) return null;

    return (
      <div style={{ position: 'relative' }} data-team-filter-dropdown>
        <button
          onClick={() => { setTeamFilterMenuOpen(!teamFilterMenuOpen); setViewDropdownOpen(false); }}
          style={{
            padding: isMobile ? '6px 10px' : '8px 12px',
            backgroundColor: selectedTeams.length > 0 ? '#3b82f6' : 'white',
            color: selectedTeams.length > 0 ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: '500',
            fontSize: isMobile ? '12px' : '14px'
          }}
        >
          <span>🔍</span>
          <span>
            {selectedTeams.length === 0 ? 'Filter Teams' : `${selectedTeams.length} Team${selectedTeams.length !== 1 ? 's' : ''}`}
          </span>
          {selectedTeams.length > 0 && (
            <span style={{
              backgroundColor: 'rgba(255,255,255,0.3)',
              padding: '2px 6px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {selectedTeams.length}
            </span>
          )}
        </button>
        
        {teamFilterMenuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            minWidth: isMobile ? '200px' : '220px',
            maxWidth: '300px',
            zIndex: 1000,
            maxHeight: '400px',
            overflowY: 'auto',
            marginTop: '4px'
          }}>
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Filter by Teams
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={selectAllTeams}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#e0f2fe',
                    border: '1px solid #0891b2',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#0891b2',
                    cursor: 'pointer',
                    fontWeight: '500',
                    flex: 1
                  }}
                >
                  Select All
                </button>
                <button
                  onClick={clearTeamFilters}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #ef4444',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontWeight: '500',
                    flex: 1
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            
            <div style={{ padding: '8px' }}>
              {availableTeams.map(team => {
                const isSelected = selectedTeams.includes(team);
                const teamObj = teamSettings.find(t => t.name === team);
                const teamColor = teamObj?.color || '#3b82f6';
                
                return (
                  <button
                    key={team}
                    onClick={() => toggleTeamSelection(team)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: isSelected ? '#f0fdf4' : 'white',
                      color: isSelected ? '#15803d' : '#374151',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '4px',
                      marginBottom: '2px'
                    }}
                  >
                    <span style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid',
                      borderColor: isSelected ? '#15803d' : '#6b7280',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isSelected ? '#15803d' : 'white',
                      flexShrink: 0
                    }}>
                      {isSelected && (
                        <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </span>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: teamColor,
                      flexShrink: 0
                    }} />
                    <span style={{ 
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {team}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }, [availableTeams, teamFilterMenuOpen, selectedTeams, isMobile, teamSettings, toggleTeamSelection, selectAllTeams, clearTeamFilters]);

  // ============================================================================
  // RENDER STATES
  // ============================================================================
  
  // Show skeleton while loading (instead of blocking spinner)
  if (loading) {
    return <MapViewSkeleton isMobile={isMobile} />;
  }
  
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
        padding: '20px'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
            Unable to Load
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAP VIEW RENDER
  // ============================================================================
  const renderMapView = () => {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: isMobile ? '12px' : '20px'
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '20px',
          maxWidth: '1200px',
          margin: '0 auto 20px'
        }}>
          <div style={{ padding: isMobile ? '16px' : '24px' }}>
            {clubLogoUrl && (
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <img 
                  src={clubLogoUrl} 
                  alt="Club Logo"
                  loading="lazy"
                  style={{
                    maxHeight: isMobile ? '120px' : '160px',
                    maxWidth: isMobile ? '300px' : '400px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '12px'
                  }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            )}
            
            <h1 style={{
              fontSize: isMobile ? '20px' : '28px',
              fontWeight: 'bold',
              color: textColor,
              margin: 0,
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {clubName} {allocationType} Allocations
            </h1>
            
            <div style={{
              fontSize: isMobile ? '14px' : '16px',
              color: textColor,
              textAlign: 'center',
              marginTop: '8px',
              opacity: 0.9
            }}>
              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              {facilityName && ` • ${facilityName}`}
            </div>
          </div>
        </div>

        {/* Facility Switcher - Apple-style Segmented Control */}
        {allFacilities.length > 1 && (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto 20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: isMobile ? '12px' : '13px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              Select a Location
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
              <div style={{
                display: 'inline-flex',
                backgroundColor: '#f3f4f6',
                borderRadius: '10px',
                padding: '4px',
                gap: '4px'
              }}>
                {allFacilities.map((facility) => {
                  const isSelected = selectedFacilityId === facility.id;
                  return (
                    <button
                      key={facility.id}
                      onClick={() => handleFacilityChange(facility.id)}
                      style={{
                        padding: isMobile ? '10px 16px' : '12px 24px',
                        fontSize: isMobile ? '14px' : '15px',
                        fontWeight: '600',
                        color: isSelected ? '#ffffff' : '#374151',
                        backgroundColor: isSelected ? primaryColor : 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: isSelected ? '0 2px 4px rgba(36, 54, 101, 0.3)' : 'none',
                        whiteSpace: 'nowrap',
                        minWidth: isMobile ? 'auto' : '160px'
                      }}
                    >
                      {facility.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Satellite Map */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Hidden image for loading - ALWAYS MOUNTED so ZoomedPitchCanvas can use it */}
          {sharedData?.satelliteConfig?.imageUrl && (
            <img
              ref={imageRef}
              src={sharedData.satelliteConfig.imageUrl}
              alt="Satellite"
              onLoad={handleImageLoad}
              style={{ display: 'none' }}
              crossOrigin="anonymous"
            />
          )}
          
          {/* Zoomed Pitch Confirmation (Mobile Only) */}
          {zoomedPitch && isMobile ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              {/* Zoomed canvas showing just this pitch */}
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '350px',
                aspectRatio: '1',
                backgroundColor: '#f0f0f0',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '3px solid #22c55e'
              }}>
                <ZoomedPitchCanvas
                  imageRef={imageRef}
                  pitchData={zoomedPitch.pitchData}
                  satelliteConfig={sharedData.satelliteConfig}
                  displayName={zoomedPitch.displayName}
                  fullPitchData={zoomedPitch.fullPitchData}
                />
              </div>
              
              {/* Pitch info */}
              <div style={{ textAlign: 'center' }}>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '8px'
                }}>
                  {zoomedPitch.displayName}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  {(() => {
                    const allocs = getPitchAllocations(zoomedPitch.pitchNumber);
                    const count = Object.keys(allocs).length;
                    return count > 0 ? `${count} allocation${count !== 1 ? 's' : ''} today` : 'No allocations today';
                  })()}
                </p>
              </div>
              
              {/* Action buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                width: '100%',
                maxWidth: '350px'
              }}>
                <button
                  onClick={() => setZoomedPitch(null)}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '2px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ← Back
                </button>
                <button
                  onClick={() => {
                    setSelectedPitch(zoomedPitch.pitchNumber);
                    setViewMode('pitch');
                    setZoomedPitch(null);
                  }}
                  style={{
                    flex: 2,
                    padding: '14px 20px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  View Allocations →
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Tap a pitch to view allocations
              </h2>
              
              {/* Mobile scroll hint */}
              {isMobile && imageLoaded && (
                <div style={{
                  textAlign: 'center',
                  marginBottom: '12px',
                  padding: '8px 12px',
                  fontSize: '13px',
                  color: '#1f2937',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>☝️</span>
                  <span><strong>Swipe</strong> to explore the map, <strong>tap</strong> a pitch to select</span>
                </div>
              )}
              
              {sharedData?.satelliteConfig?.imageUrl ? (
                <>
                  {/* Scrollable map container for mobile */}
                  <div 
                    ref={mapScrollContainerRef}
                    style={{
                      position: 'relative',
                      width: '100%',
                      maxHeight: isMobile ? '450px' : 'none',
                      overflow: isMobile ? 'auto' : 'visible',
                      borderRadius: '12px',
                      border: '2px solid #d1d5db',
                      backgroundColor: '#f0f0f0',
                      WebkitOverflowScrolling: 'touch',
                      boxShadow: isMobile ? 'inset 0 2px 8px rgba(0,0,0,0.1)' : 'none'
                    }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: isMobile ? 'flex-start' : 'center',
                      alignItems: 'center',
                      minHeight: isMobile ? '250px' : '500px',
                      padding: isMobile ? '0' : '0'
                    }}>
                    
                    {/* Loading placeholder */}
                    {!imageLoaded && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          border: '4px solid #e5e7eb',
                          borderTop: '4px solid #3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        <style>{`
                          @keyframes spin {
                            to { transform: rotate(360deg); }
                          }
                        `}</style>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>Loading map...</span>
                      </div>
                    )}
                    
                    {/* Canvas */}
                    <canvas
                      ref={canvasRef}
                      onClick={handleCanvasClick}
                      onTouchStart={(e) => {
                        // Use touchStart for more responsive feel on mobile
                        if (isMobile) {
                          handleCanvasClick(e);
                        }
                      }}
                      style={{
                        display: imageLoaded ? 'block' : 'none',
                        cursor: 'pointer',
                        borderRadius: isMobile ? '0' : '8px',
                        touchAction: isMobile ? 'pan-x pan-y' : 'manipulation'
                      }}
                    />
                    </div>
                  </div>
                  
                  {/* Pitch Selection Buttons */}
                  {pitchBoundaries.length > 0 && (
                    <div id="pitch-buttons-section" style={{
                      marginTop: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <h3 style={{
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 'bold',
                        color: '#1f2937',
                        marginBottom: '8px',
                        textAlign: 'center'
                      }}>
                        Or select a pitch:
                      </h3>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '12px'
                      }}>
                        {pitchBoundaries.map((pitch, index) => {
                          const pitchNumber = pitch.pitchNumber || String(index + 1);
                          const displayName = getPitchDisplayName(pitchNumber, pitchNames);
                          const allocations = getPitchAllocations(pitchNumber);
                          const allocCount = Object.keys(allocations).length;
                          const hasAllocations = allocCount > 0;
                          
                          return (
                            <button
                              key={pitchNumber}
                              onClick={() => {
                                setSelectedPitch(pitchNumber);
                                setViewMode('pitch');
                              }}
                              style={{
                                padding: '16px',
                                backgroundColor: 'white',
                                border: hasAllocations 
                                  ? '2px solid #22c55e' 
                                  : '2px solid #d1d5db',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.2s ease',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                            >
                              <div style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: hasAllocations 
                                  ? '#22c55e' 
                                  : '#9ca3af',
                                color: 'white',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                flexShrink: 0
                              }}>
                                {pitchNumber}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#1f2937',
                                  marginBottom: '2px'
                                }}>
                                  {displayName}
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#6b7280'
                                }}>
                                  {hasAllocations ? `${allocCount} allocations` : 'No allocations'}
                                </div>
                              </div>
                              <div style={{
                                fontSize: '20px',
                                color: '#9ca3af'
                              }}>
                                →
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Fallback pitch list if no satellite image
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  {pitchBoundaries.map((pitch, index) => {
                    const pitchNumber = pitch.pitchNumber || String(index + 1);
                    const displayName = getPitchDisplayName(pitchNumber, pitchNames);
                    const allocations = getPitchAllocations(pitchNumber);
                    const allocCount = Object.keys(allocations).length;
                    const hasAllocations = allocCount > 0;
                    
                    return (
                      <button
                        key={pitchNumber}
                        onClick={() => {
                          setSelectedPitch(pitchNumber);
                          setViewMode('pitch');
                        }}
                        style={{
                          padding: '20px',
                          backgroundColor: '#f0fdf4',
                          border: hasAllocations ? '2px solid #22c55e' : '2px solid #d1d5db',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚽</div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#166534' }}>
                          {displayName}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                          {hasAllocations ? `${allocCount} allocations` : 'No allocations'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Sponsor */}
        {sponsorLogoUrl && (
          <div style={{
            maxWidth: '1200px',
            margin: '20px auto',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', fontWeight: '500' }}>
              Brought to you by our sponsor
            </div>
            <div
              onClick={() => {
                if (sponsorUrl) {
                  window.open(sponsorUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              style={{ cursor: sponsorUrl ? 'pointer' : 'default', display: 'flex', justifyContent: 'center' }}
            >
              <img 
                src={sponsorLogoUrl}
                alt="Sponsor Logo"
                loading="lazy"
                style={{
                  maxHeight: isMobile ? '60px' : '80px',
                  maxWidth: isMobile ? '200px' : '300px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = 'none'; }}
              />
            </div>
          </div>
        )}
        
        {/* Powered by Pitchallo */}
        <div style={{
          maxWidth: '1200px',
          margin: '16px auto',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <img 
            src="/poweredby.jpg"
            alt="Powered by Pitchallo"
            loading="lazy"
            style={{
              maxHeight: isMobile ? '120px' : '150px',
              width: 'auto'
            }}
          />
        </div>
        
        <PitchalloCTA isMobile={isMobile} />
      </div>
    );
  };

  // ============================================================================
  // PITCH VIEW RENDER
  // ============================================================================
  const renderPitchView = () => {
    if (!selectedPitch) return null;
    
    const pitchId = `pitch-${selectedPitch}`;
    const allocations = getPitchAllocations(selectedPitch);
    const timeSlots = getTimeSlotsForPitch(selectedPitch);
    const pitchConfig = pitchConfigs[pitchId] || pitchConfigs[`pitch${selectedPitch}`] || null;
    const displayName = getPitchDisplayName(selectedPitch, pitchNames);
    
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: isMobile ? '12px' : '20px'
      }}>
        {/* Navy blue banner with back and print buttons */}
        <div style={{
          backgroundColor: '#243665',
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderRadius: '12px',
          marginBottom: '20px',
          maxWidth: '1200px',
          margin: '0 auto 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => {
              setViewMode('map');
              setSelectedPitch(null);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            ← Back to Pitch Selection
          </button>
          
          <button
            onClick={() => window.print()}
            style={{
              padding: '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            🖨️ Print
          </button>
        </div>
        
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          overflow: 'visible',
          marginBottom: '20px',
          maxWidth: '1200px',
          margin: '0 auto 20px'
        }}>
          <div style={{ padding: isMobile ? '16px' : '24px' }}>
            {clubLogoUrl && (
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <img 
                  src={clubLogoUrl} 
                  alt="Club Logo"
                  loading="lazy"
                  style={{
                    maxHeight: isMobile ? '120px' : '160px',
                    maxWidth: isMobile ? '300px' : '400px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '12px'
                  }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            )}
            
            <h1 style={{
              fontSize: isMobile ? '20px' : '28px',
              fontWeight: 'bold',
              color: textColor,
              margin: 0,
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {displayName}
            </h1>
            
            <div style={{
              fontSize: isMobile ? '14px' : '16px',
              color: textColor,
              textAlign: 'center',
              marginTop: '8px'
            }}>
              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })} - {Object.keys(allocations).length} allocations
              {selectedTeams.length > 0 && (
                <span style={{ opacity: 0.9 }}>
                  {' '}(filtered by {selectedTeams.length} team{selectedTeams.length !== 1 ? 's' : ''})
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Team Filter + View Interval Controls */}
        {availableTeams.length > 0 && (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto 20px',
            padding: isMobile ? '12px' : '16px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {renderTeamFilter()}

            {/* View interval dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setViewDropdownOpen(v => !v); setTeamFilterMenuOpen(false); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap'
                }}
              >
                🕐 {intervalMinutes === 15 ? '15 min' : intervalMinutes === 30 ? '30 min' : '1 hour'}
                <span style={{ fontSize: '10px', opacity: 0.6 }}>▾</span>
              </button>

              {viewDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  zIndex: 100,
                  minWidth: '140px',
                  overflow: 'hidden'
                }}>
                  {[
                    { label: '15 min intervals', value: 15 },
                    { label: '30 min intervals', value: 30 },
                    { label: 'Hourly', value: 60 }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setIntervalMinutes(opt.value); setViewDropdownOpen(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        textAlign: 'left',
                        background: intervalMinutes === opt.value ? '#eff6ff' : 'white',
                        border: 'none',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: intervalMinutes === opt.value ? '600' : '400',
                        color: intervalMinutes === opt.value ? '#3b82f6' : '#374151'
                      }}
                    >
                      {intervalMinutes === opt.value ? '✓ ' : '   '}{opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {timeSlots.length === 0 ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '40px 20px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                No Allocations
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                This pitch has no allocations for {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {groupTimeSlots(timeSlots, intervalMinutes).map((group) => {
                // For grouped intervals, use the first slot's allocations as representative
                // but show all slots' data merged
                const representativeSlot = group.slots[0];

                return (
                  <div key={group.label} style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '20px',
                    paddingBottom: isMobile ? '40px' : '50px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <h3 style={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '32px',
                      textAlign: 'center'
                    }}>
                      {group.label}
                    </h3>
                    
                    <ReadOnlyPitchRenderer
                      pitchSectionConfig={pitchConfig}
                      timeSlot={representativeSlot}
                      allocations={allocations}
                      date={date}
                      pitchId={pitchId}
                      teams={teamSettings}
                      groupSlots={group.slots}
                      pitchReferencePoints={sharedData?.pitchReferencePoints?.[pitchId] || null}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Sponsor */}
        {sponsorLogoUrl && (
          <div style={{
            maxWidth: '1200px',
            margin: '20px auto',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', fontWeight: '500' }}>
              Brought to you by our sponsor
            </div>
            <div
              onClick={() => {
                if (sponsorUrl) {
                  window.open(sponsorUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              style={{ cursor: sponsorUrl ? 'pointer' : 'default', display: 'flex', justifyContent: 'center' }}
            >
              <img 
                src={sponsorLogoUrl}
                alt="Sponsor Logo"
                loading="lazy"
                style={{
                  maxHeight: isMobile ? '60px' : '80px',
                  maxWidth: isMobile ? '200px' : '300px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = 'none'; }}
              />
            </div>
          </div>
        )}
        
        {/* Powered by Pitchallo */}
        <div style={{
          maxWidth: '1200px',
          margin: '16px auto',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <img 
            src="/poweredby.jpg"
            alt="Powered by Pitchallo"
            loading="lazy"
            style={{
              maxHeight: isMobile ? '120px' : '150px',
              width: 'auto'
            }}
          />
        </div>
        
        <PitchalloCTA isMobile={isMobile} />
      </div>
    );
  };

  return viewMode === 'map' ? renderMapView() : renderPitchView();
}

export default ShareView;
